<script setup lang="ts">
import GlobalSnackbar from "@/components/shared/GlobalSnackbar.vue";
import JitsiFloat from "@/components/integrations/jitsi/JitsiFloat.vue";
import { useLayout } from "@/composables/shared/useLayout";
import { useAuthStore } from "@/stores/authStore";
import { useStaffSubscription } from "@/composables/models/websocket/subscriptions/staffSubscription";
import { computed, onMounted, ref, watch, onUnmounted } from "vue";
import { storeToRefs } from "pinia";
import { useTheme } from "vuetify";
import { useRoute, useRouter } from "vue-router";
import { useSettingsStore } from "@/stores/settingsStore";
import { useJitsiStore } from "./stores/jitsiStore";
import { usePendingTermsStore } from "@/stores/pendingTermsStore";
import { usePracticeStaffSettings } from "@/composables/models/practice/usePracticeStaffSettings";
import { EPracticeStaffSettingsKey } from "@/api/models/practice/practiceStaffSettings";
import {
  tourDialogVisible,
  tutorialDialogDismissedThisSession,
  useProductTour,
  type TourId,
} from "@/composables/shared/useProductTour";
import SnackDialog from "@/components/shared/SnackDialog.vue";
import PendingTermsDialog from "@/components/shared/dialogs/PendingTermsDialog.vue";
import TutorialSelectDialog from "@/components/shared/TutorialSelectDialog.vue";
import UserAvatar from "@/components/shared/UserAvatar.vue";
import { useVersionUpdatePolling } from "@/composables/shared/useVersionUpdatePolling";
import DevToolsHost from "@/components/dev/DevToolsHost.vue";
import { useAppStateStore } from "@/stores/appStateStore";

const theme = useTheme();
const settingsStore = useSettingsStore();
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const { startTour } = useProductTour();
const { startPolling, stopPolling } = useVersionUpdatePolling();

const { loggedAsStaff, isLoggedIn } = storeToRefs(authStore);
const { isBlocked: isBlockedByPendingTerms } = storeToRefs(usePendingTermsStore());

// Grace window so the pending-terms 403 interceptor has a chance to block before
// the tutorial dialog auto-opens on login; otherwise it flashes behind the terms dialog.
const INITIAL_AUTO_SHOW_DELAY_MS = 600;
const isInitialAutoShowGateReady = ref(false);
let initialAutoShowTimeoutId: ReturnType<typeof setTimeout> | null = null;

watch(
  isLoggedIn,
  (loggedIn) => {
    if (initialAutoShowTimeoutId !== null) {
      clearTimeout(initialAutoShowTimeoutId);
      initialAutoShowTimeoutId = null;
    }
    if (!loggedIn) {
      stopPolling();
      isInitialAutoShowGateReady.value = false;
      tutorialDialogDismissedThisSession.value = false;
      return;
    }
    isInitialAutoShowGateReady.value = false;
    initialAutoShowTimeoutId = setTimeout(() => {
      isInitialAutoShowGateReady.value = true;
      initialAutoShowTimeoutId = null;
    }, INITIAL_AUTO_SHOW_DELAY_MS);

    startPolling();
  },
  { immediate: true }
);

const shouldAutoShowTutorial = computed(() => {
  if (!isLoggedIn.value) return false;
  if (isBlockedByPendingTerms.value) return false;
  if (!isInitialAutoShowGateReady.value) return false;
  if (tutorialDialogDismissedThisSession.value) return false;
  const staff = authStore.authUser?.logged_staff;
  if (!staff?.id) return false;
  const seen =
    staff.user_settings?.prefferences_v1?.[EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN];
  return seen !== true;
});

const handleStartTutorial = async (tourId: TourId) => {
  settingsStore[EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN] = true;
  tourDialogVisible.value = false;
  if (tourId === "dashboard-main") {
    await router.push("/");
  }
  startTour(tourId, { force: true });
};

usePracticeStaffSettings();

const { unsubscribeStaff } = useStaffSubscription();

const { prepareId } = storeToRefs(useJitsiStore());

const { layout, layoutComponent } = useLayout();
const isRouterReady = ref(false);

const { isKeyboardActive, keyboardHeight } = storeToRefs(useAppStateStore());
watch(isKeyboardActive, (active) => {
  document.body.style.paddingBottom = active ? `${keyboardHeight.value}px` : "";
  if (!active) return;

  const activeEl = document.activeElement as HTMLElement | null;
  const isTextEntry =
    activeEl &&
    (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA" || activeEl.isContentEditable);
  if (!isTextEntry) return;

  // Let the padding-driven layout reflow settle before scrolling.
  setTimeout(() => {
    activeEl?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, 50);
});

onMounted(async () => {
  await router.isReady();
  isRouterReady.value = true;

  if ("virtualKeyboard" in navigator) {
    (navigator as any)["virtualKeyboard"].overlaysContent = true;
  }
});

onUnmounted(() => {
  stopPolling();
  authStore.tokenVerificationInterval("stop");
  if (loggedAsStaff.value?.id) {
    unsubscribeStaff(loggedAsStaff.value?.id);
  }
});

watch(
  () => settingsStore.isDarkMode,
  (isDark) => {
    theme.change(isDark ? "orbisDarkTheme" : "orbisLightTheme");
    document.documentElement.classList.toggle("dark", isDark);
  },
  { immediate: true }
);

const showHelpers = computed(
  () => isLoggedIn && !route.path.startsWith("/documents") && route.path !== "/terms-and-conditions"
);
</script>

<template>
  <v-app class="tw:font-sans">
    <v-overlay
      :model-value="authStore.isRoleSwitching"
      persistent
      class="tw:items-center tw:justify-center"
      content-class="tw:w-full tw:h-full tw:flex tw:items-center tw:justify-center tw:bg-surface"
    >
      <div class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-4 tw:px-8 tw:py-7">
        <template v-if="authStore.authUser?.logged_staff">
          <user-avatar
            :user="authStore?.authUser?.logged_staff"
            size="74"
            class="tw:!rounded-xl"
            hide-badges
          />

          <div class="tw:text-2xl tw:font-semibold">
            {{ authStore.roleSwitchLabel }}
          </div>
        </template>

        <loading
          v-else
          :size="60"
          :label="authStore.roleSwitchLabel"
        />
      </div>
    </v-overlay>

    <snack-dialog />
    <pending-terms-dialog v-if="showHelpers" />
    <tutorial-select-dialog
      v-if="showHelpers && !isBlockedByPendingTerms"
      :model-value="tourDialogVisible || shouldAutoShowTutorial"
      @update:model-value="(v: boolean) => (tourDialogVisible = v)"
      @start="handleStartTutorial"
    />
    <jitsi-float v-if="prepareId" />
    <component
      v-if="isRouterReady"
      :is="layoutComponent"
      :key="layout.name"
    />

    <dev-tools-host />
  </v-app>

  <GlobalSnackbar />
</template>
